// src/utils/computeDutchRelation.js

/**
 * Berekent een Nederlandse relatieomschrijving op basis van een 
 * relatiepad-array. Elk element in het pad bevat een "label" (bijv. "vader", "moeder", etc.)
 * Dit is een uitgebreidere implementatie die tot 20 generaties doorrekent en gender gebruikt.
 */
export function computeDutchRelation(path) {
    // Zorg ervoor dat er minimaal twee elementen in het pad zitten (start en target)
    if (!path || path.length < 2) return "Zelfde persoon";

    // Reduce each hop in the path to a directional token so we can reason about
    // family distance without depending on every raw relationship label variant.
    const directions = path.slice(1).map(step => interpretLabel(step.label));

    // Tel het aantal UP en DOWN stappen, en check op SIDE en SPOUSE.
    let upCount = 0;
    let downCount = 0;
    let hasSide = false;
    let hasSpouse = false;

    directions.forEach(dir => {
        if (dir === "UP") upCount++;
        else if (dir === "DOWN") downCount++;
        else if (dir === "SIDE") hasSide = true;
        else if (dir === "SPOUSE") hasSpouse = true;
    });

    // SIDE means the path crosses a sibling branch, so direct ancestor/descendant
    // naming no longer applies and we switch to uncle/aunt or cousin-like output.
    if (hasSide) {
        // Voor deze eenvoudige variant gebruiken we de UP-telling om te kiezen.
        if (upCount === 1) return hasSpouse ? "oom/tante (aangetrouwd)" : "oom/tante";
        if (upCount === 2) return "oud-oom/tante";
        if (upCount === 3) return "overgrootoom/tante";
        return "verre oom/tante of neef/nicht";
    }

    // The last node represents the target person, whose gender is used only for
    // gendered relation terms where Dutch distinguishes male/female variants.
    const targetGender = path[path.length - 1].sex ? path[path.length - 1].sex.toUpperCase() : "M";

    // Als het pad alleen uit UP-stappen bestaat (directe voorouders)
    if (upCount > 0 && downCount === 0) {
        return makeUpName(upCount, hasSpouse, targetGender);
    }

    // Als het pad alleen uit DOWN-stappen bestaat (directe nakomelingen)
    if (downCount > 0 && upCount === 0) {
        return makeDownName(downCount, hasSpouse, targetGender);
    }

    // Anders: een gemengd pad; vermoedelijk een neef/nicht relatie (cousinship)
    return guessCousinRelationship(upCount, downCount, hasSpouse, targetGender);
}


// Ondersteunende hulpfuncties

function interpretLabel(label) {
    // Map backend labels to a compact directional vocabulary used by this file.
    if (["vader", "moeder", "parent"].includes(label.toLowerCase())) return "UP";
    if (["zoon", "dochter", "child"].includes(label.toLowerCase())) return "DOWN";
    if (["broer", "zus", "halfbroer", "halfzus"].includes(label.toLowerCase())) return "SIDE";
    if (["spouse", "echtgenoot", "echtgenote"].includes(label.toLowerCase())) return "SPOUSE";
    return "UNKNOWN";
}

function makeUpName(generations, hasSpouse, gender) {
    // Lookup-tabellen voor voorouders (generaties 1 t/m 20)
    // Voor de eerste 5 generaties gebruiken we vaste termen; daarna een generiek patroon.
    const upMale = [
        "vader",
        "grootvader",
        "overgrootvader",
        "betovergrootvader",
        "oudvader",
        "oudgrootvader",
        "oudovergrootvader",
        "oudbetovergrootvader",
        "stamvader",
        "stamgrootvader",
        "stamovergrootvader",
        "stambetovergrootvader",
        "stamoudvader",
        "stamoudgrootvader",
        "stambetovergrootvader",
        "stamoudbetovergrootvader",
        "edelvader",
        "edelgrootvader",
        "edelovergrootvader",
        "edelbetovergrootvader"
    ];
    const upFemale = [
        "moeder",
        "grootmoeder",
        "overgrootmoeder",
        "betovergrootmoeder",
        "oudmoeder",
        "oudgrootmoeder",
        "oudovergrootmoeder",
        "oudbetovergrootmoeder",
        "stammoeder",
        "stamgrootmoeder",
        "stamovergrootmoeder",
        "stambetovergrootmoeder",
        "stamoudmoeder",
        "stamoudgrootmoeder",
        "stambetovergrootmoeder",
        "stamoudbetovergrootmoeder",
        "edelmoeder",
        "edelgrootmoeder",
        "edelovergrootmoeder",
        "edelbetovergrootmoeder"
    ];
    const lookup = gender === 'M' ? upMale : upFemale;
    if (generations >= 1 && generations <= lookup.length) {
        // For in-range generations we return curated Dutch terms instead of a formula.
        return hasSpouse ? "schoon" + lookup[generations - 1] : lookup[generations - 1];
    }
    return "verre voorouder (+" + generations + " generaties)" + (hasSpouse ? " (aangetrouwd)" : "");
}

function makeDownName(generations, hasSpouse, gender) {
    // Lookup-tabellen voor nakomelingen; voor generatie 1 gebruiken we gender-specifieke termen.
    const downMale = [
        "zoon",
        "kleinkind",
        "achterkleinkind",
        "achterachterkleinkind",
        "5e nakomeling",
        "6e nakomeling",
        "7e nakomeling",
        "8e nakomeling",
        "9e nakomeling",
        "10e nakomeling",
        "11e nakomeling",
        "12e nakomeling",
        "13e nakomeling",
        "14e nakomeling",
        "15e nakomeling",
        "16e nakomeling",
        "17e nakomeling",
        "18e nakomeling",
        "19e nakomeling",
        "20e nakomeling"
    ];
    const downFemale = [
        "dochter",
        "kleinkind",
        "achterkleinkind",
        "achterachterkleinkind",
        "5e nakomeling",
        "6e nakomeling",
        "7e nakomeling",
        "8e nakomeling",
        "9e nakomeling",
        "10e nakomeling",
        "11e nakomeling",
        "12e nakomeling",
        "13e nakomeling",
        "14e nakomeling",
        "15e nakomeling",
        "16e nakomeling",
        "17e nakomeling",
        "18e nakomeling",
        "19e nakomeling",
        "20e nakomeling"
    ];
    const lookup = gender === 'M' ? downMale : downFemale;
    if (generations >= 1 && generations <= lookup.length) {
        return hasSpouse ? "schoon" + lookup[generations - 1] : lookup[generations - 1];
    }
    return "verre nakomeling (+" + generations + " generaties)" + (hasSpouse ? " (aangetrouwd)" : "");
}

function guessCousinRelationship(up, down, hasSpouse, gender) {
    // Bereken het "cousin degree" volgens de conventie:
    // 1e graden (cousinDegree 1) als zowel up als down gelijk zijn, maar -1 omdat de eerste stap niet meetelt.
    const cousinDegree = Math.min(up, down) - 1;
    // Verschil tussen omhoog en omlaag = aantal generaties verwijderd.
    const removed = Math.abs(up - down);

    // Als er geen echte “cousin” relatie is (dus als de kleinere waarde 1 is):
    if (cousinDegree < 1) {
        // This branch covers asymmetric cases that are closer to uncle/aunt or
        // nephew/niece than to true cousin degrees.
        // Als je meer UP-stappen hebt dan DOWN, dan is het een oom/tante relatie.
        if (up > down) {
            return constructUncleAunt(up - down, hasSpouse, gender);
        } else {
            // Anders geldt het als neef/nicht.
            return constructNephewNiece(down - up, hasSpouse, gender);
        }
    }

    // Voor echte cousin-relaties (cousinDegree >= 1)
    let cousinStr = "";
    if (cousinDegree === 1) {
        cousinStr = gender === 'M' ? "1e neef" : "1e nicht";
    } else if (cousinDegree === 2) {
        cousinStr = gender === 'M' ? "2e neef" : "2e nicht";
    } else if (cousinDegree === 3) {
        cousinStr = gender === 'M' ? "3e neef" : "3e nicht";
    } else if (cousinDegree === 4) {
        cousinStr = gender === 'M' ? "4e neef" : "4e niet";
    } else {
        cousinStr = cousinDegree + "e graads neef/nicht";
    }

    if (removed > 0) {
        // "Removed" counts generation offset relative to an equal-degree cousin.
        cousinStr += `, ${removed}× verwijderd`;
    }
    if (hasSpouse) {
        cousinStr += " (aangetrouwd)";
    }
    return cousinStr;
}

/**
 * Geeft een omschrijving voor een oom/tante-relatie op basis van hoeveel generaties
 * boven sibling-niveau de relatie ligt.
 *
 * @param {number} diff - Het aantal generaties verschil (up - down).
 * @param {boolean} hasSpouse - Of er een aangetrouwde relatie is.
 * @param {string} gender - 'M' of 'F'.
 * @returns {string} - Bijv. "oom", "oud-oom", "overgrootoom", enz.
 */
function constructUncleAunt(diff, hasSpouse, gender) {
    // Lookup-tabellen voor oom/tante.
    const titlesMale = ["oom", "oud-oom", "overgrootoom", "betovergrootoom"];
    const titlesFemale = ["tante", "oud-tante", "overgroottante", "betovergroottante"];
    let title = "";
    if (diff >= 1 && diff <= titlesMale.length) {
        title = gender === 'M' ? titlesMale[diff - 1] : titlesFemale[diff - 1];
    } else {
        title = gender === 'M' ? diff + " generaties verre oom" : diff + " generaties verre tante";
    }
    if (hasSpouse) title += " (aangetrouwd)";
    return title;
}

/**
 * Geeft een omschrijving voor een neef/nicht-relatie op basis van hoeveel generaties
 * verwijderd de relatie is ten opzichte van een directe sibling.
 *
 * @param {number} diff - Het aantal generaties verschil (down - up).
 * @param {boolean} hasSpouse - Of er een aangetroude relatie is.
 * @param {string} gender - 'M' of 'F'.
 * @returns {string} - Bijv. "neef", "oud-neef", "overgroot-neef", enz.
 */
function constructNephewNiece(diff, hasSpouse, gender) {
    // Lookup-tabellen voor neef/nicht.
    const titlesMale = ["neef", "oud-neef", "overgroot-neef", "betovergroot-neef"];
    const titlesFemale = ["nicht", "oud-nicht", "overgroot-nicht", "betovergroot-nicht"];
    let title = "";
    if (diff >= 1 && diff <= titlesMale.length) {
        title = gender === 'M' ? titlesMale[diff - 1] : titlesFemale[diff - 1];
    } else {
        title = gender === 'M' ? diff + " generaties verre neef" : diff + " generaties verre nicht";
    }
    if (hasSpouse) title += " (aangetrouwd)";
    return title;
}

// Exporteer de functie zodat je die in je Vue-component kunt gebruiken.
export { guessCousinRelationship };

